// src/edu-world/systems/warm-welcome.ts
// Warm Welcome System — ensures each session begins with immediate access to a familiar NPC
// Standalone module (Phase 3) — pure logic, Phaser-agnostic
// Avoids cold starts where the child must search for something to do
// Requirements: 22.8

// ─── Types ───

export type GreetingType =
  | 'first_session'
  | 'returning_player'
  | 'returning_to_familiar_npc';

export interface WelcomeConfig {
  /** NPC to highlight/position near spawn so child sees them within 5 seconds of movement */
  npcToHighlight: string;
  /** Type of greeting to use based on session history */
  greetingType: GreetingType;
  /** Suggested path from spawn point toward the highlighted NPC */
  suggestedPlayerPath: { x: number; y: number }[];
}

export interface WarmWelcomeConfig {
  /** Default NPC to use when no history is available */
  defaultNpcId: string;
  /** All available NPCs that can serve as welcome targets */
  availableNpcIds: string[];
  /** Maximum time (ms) from first movement to reaching a familiar NPC (default: 5000) */
  maxTimeToNpcMs: number;
  /** Player movement speed in px/s (used to calculate path feasibility) */
  playerSpeed: number;
  /** Spawn point position */
  spawnPoint: { x: number; y: number };
  /** NPC positions keyed by NPC id */
  npcPositions: Record<string, { x: number; y: number }>;
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: WarmWelcomeConfig = {
  defaultNpcId: 'rosa',
  availableNpcIds: ['rosa', 'tome'],
  maxTimeToNpcMs: 5000,
  playerSpeed: 200, // px/s
  spawnPoint: { x: 400, y: 400 },
  npcPositions: {
    rosa: { x: 600, y: 350 },
    tome: { x: 500, y: 250 },
  },
};

// ─── WarmWelcome ───

/**
 * Ensures each session begins with immediate access to a familiar NPC
 * within 5 seconds of movement from the start position.
 *
 * Key rules:
 * - Child must be able to reach a familiar NPC within 5 seconds of first movement
 * - Avoids cold starts where the child must search for something to do
 * - Prioritizes the last NPC the child interacted with (familiarity)
 * - Falls back to default NPC (Rosa) for first sessions
 */
export class WarmWelcome {
  private config: WarmWelcomeConfig;

  constructor(config?: Partial<WarmWelcomeConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Get the welcome configuration for this session start.
   *
   * @param sessionCount - How many sessions the child has played (1 = first session)
   * @param lastNPC - The ID of the last NPC the child interacted with (null if first session)
   * @returns WelcomeConfig with NPC to highlight, greeting type, and suggested path
   */
  getWelcomeConfig(sessionCount: number, lastNPC: string | null): WelcomeConfig {
    const greetingType = this.determineGreetingType(sessionCount, lastNPC);
    const npcToHighlight = this.selectWelcomeNpc(sessionCount, lastNPC);
    const suggestedPlayerPath = this.computeSuggestedPath(npcToHighlight);

    return {
      npcToHighlight,
      greetingType,
      suggestedPlayerPath,
    };
  }

  // ─── Private ───

  /**
   * Determine the greeting type based on session history.
   */
  private determineGreetingType(sessionCount: number, lastNPC: string | null): GreetingType {
    if (sessionCount <= 1) {
      return 'first_session';
    }

    if (lastNPC && this.config.availableNpcIds.includes(lastNPC)) {
      return 'returning_to_familiar_npc';
    }

    return 'returning_player';
  }

  /**
   * Select the NPC to highlight at session start.
   * Prioritizes the last interacted NPC for familiarity.
   * Falls back to the default NPC (Rosa) for first sessions.
   * Validates that the NPC is reachable within the time constraint.
   */
  private selectWelcomeNpc(sessionCount: number, lastNPC: string | null): string {
    // For returning players, try to use the last NPC they interacted with
    if (sessionCount > 1 && lastNPC && this.isNpcReachableInTime(lastNPC)) {
      return lastNPC;
    }

    // Check if default NPC is reachable
    if (this.isNpcReachableInTime(this.config.defaultNpcId)) {
      return this.config.defaultNpcId;
    }

    // Find the closest reachable NPC
    const closest = this.findClosestNpc();
    return closest ?? this.config.defaultNpcId;
  }

  /**
   * Check if an NPC can be reached within the maximum time constraint.
   */
  private isNpcReachableInTime(npcId: string): boolean {
    const npcPos = this.config.npcPositions[npcId];
    if (!npcPos) return false;

    const distance = this.calculateDistance(this.config.spawnPoint, npcPos);
    const timeToReachMs = (distance / this.config.playerSpeed) * 1000;

    return timeToReachMs <= this.config.maxTimeToNpcMs;
  }

  /**
   * Find the closest NPC to the spawn point that's within the time limit.
   */
  private findClosestNpc(): string | null {
    let closestId: string | null = null;
    let closestDistance = Infinity;

    for (const npcId of this.config.availableNpcIds) {
      const npcPos = this.config.npcPositions[npcId];
      if (!npcPos) continue;

      const distance = this.calculateDistance(this.config.spawnPoint, npcPos);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestId = npcId;
      }
    }

    return closestId;
  }

  /**
   * Compute a simple suggested path from spawn point to the target NPC.
   * Returns intermediate waypoints the player could follow (straight line with midpoint).
   */
  private computeSuggestedPath(npcId: string): { x: number; y: number }[] {
    const npcPos = this.config.npcPositions[npcId];
    if (!npcPos) {
      return [this.config.spawnPoint];
    }

    const spawn = this.config.spawnPoint;

    // Simple direct path: spawn → midpoint → NPC
    const midpoint = {
      x: Math.round((spawn.x + npcPos.x) / 2),
      y: Math.round((spawn.y + npcPos.y) / 2),
    };

    return [spawn, midpoint, npcPos];
  }

  /**
   * Calculate Euclidean distance between two points.
   */
  private calculateDistance(
    a: { x: number; y: number },
    b: { x: number; y: number }
  ): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
